import React, { createContext, useContext, useState, useCallback } from 'react'
import { authHeaders } from '../utils/authFetch'
import { getBaristasOffline, cacheBranchBaristasAndSchedules } from '../offline/posOfflineHelpers'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
const BaristaContext = createContext(null)

export function BaristaProvider({ children }) {
    const [selectedBarista, setSelectedBaristaState] = useState(null)
    const [branchBaristas, setBranchBaristas] = useState([])
    const [baristasLoading, setBaristasLoading] = useState(false)

    const loadBaristas = useCallback(async (branchId) => {
        if (!branchId) return
        setBaristasLoading(true)
        try {
            if (!navigator.onLine) {
                const cached = await getBaristasOffline(branchId)
                setBranchBaristas(cached.map(b => ({ ...b, has_pin: !!b.pin_hash })))
                return
            }
            const res = await fetch(`${BASE_URL}/api/barista/branch/${branchId}`, { headers: authHeaders() })
            if (!res.ok) {
                const cached = await getBaristasOffline(branchId)
                setBranchBaristas(cached.map(b => ({ ...b, has_pin: !!b.pin_hash })))
                return
            }
            const data = await res.json()
            setBranchBaristas(data)

            // Warm IndexedDB immediately (pin hashes, schedules, this month's attendance)
            // so switching baristas and clocking in/out keep working the instant the
            // connection drops — fire-and-forget, don't block the UI on it.
            cacheBranchBaristasAndSchedules(branchId).catch(err =>
                console.error('Failed to warm barista offline cache:', err)
            )
        } catch (err) {
            console.error('Error fetching baristas:', err)
            const cached = await getBaristasOffline(branchId)
            setBranchBaristas(cached.map(b => ({ ...b, has_pin: !!b.pin_hash })))
        } finally {
            setBaristasLoading(false)
        }
    }, [])

    // Returns { success, error?, needsSetup? }
    const verifyBaristaPin = useCallback(async (baristaId, pin) => {
        try {
            const res = await fetch(`${BASE_URL}/api/barista/verify-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ baristaId, pin }),
            })
            if (!res.ok) {
                return { success: false, error: 'Could not verify PIN. Please try again.' }
            }
            return await res.json()
        } catch (err) {
            console.error('Error verifying barista PIN:', err)
            return { success: false, error: 'Could not verify PIN. Please try again.' }
        }
    }, [])

    // First-time PIN creation for a barista with no pin_hash yet
    const setupBaristaPin = useCallback(async (baristaId, pin) => {
        try {
            const res = await fetch(`${BASE_URL}/api/barista/setup-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ baristaId, pin }),
            })
            const data = await res.json()
            if (!res.ok) return { success: false, error: data.error || 'Failed to set up PIN.' }
            return data
        } catch (err) {
            console.error('Error setting up barista PIN:', err)
            return { success: false, error: err.message || 'Failed to set up PIN.' }
        }
    }, [])

    const setSelectedBarista = useCallback((barista) => {
        setSelectedBaristaState(barista)
    }, [])

    const clearSelectedBarista = useCallback(() => {
        setSelectedBaristaState(null)
    }, [])

    return (
        <BaristaContext.Provider value={{
            selectedBarista,
            setSelectedBarista,
            clearSelectedBarista,
            branchBaristas,
            baristasLoading,
            loadBaristas,
            verifyBaristaPin,
            setupBaristaPin,
        }}>
            {children}
        </BaristaContext.Provider>
    )
}

export function useBarista() {
    const ctx = useContext(BaristaContext)
    if (!ctx) {
        return {
            selectedBarista: null,
            setSelectedBarista: () => {},
            clearSelectedBarista: () => {},
            branchBaristas: [],
            baristasLoading: false,
            loadBaristas: async () => {},
            verifyBaristaPin: async () => ({ success: false, error: 'Not available outside barista context.' }),
            setupBaristaPin: async () => ({ success: false, error: 'Not available outside barista context.' }),
        }
    }
    return ctx
}